import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useStore } from '../../store';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import { timeHM, todayISO } from '../../utils/region';
import { openPdf, pdfThumbUrl, type PdfHandle } from '../../utils/pdfRender';
import { ATT_MAX_BYTES, ATT_MAX_FILES, attApi, type AttFile, type AttList, type AttRefusal, type AttTrail, type LinkedAnswer, type LinkedDoc } from '../../utils/api/recordAttachments';
import DlBtn from '../DlBtn';
import Pagination from '../Pagination';
import type { AwCtx } from './ctx';
import { Dialog, Loader, actBtn, field } from './dialog';
import { TAP_TARGET } from './listCells';
import { RelatedRow, typeWord, useOpenRecord } from './relatedDocs';
import { ReasonDialog } from './panes/Purchases';   // the same deliberate cycle sourceDoc.tsx's header describes: used inside a body
import { btn, card, dmy, errBox, note, sectionHead } from './ui';

// ── Every record's Attachments / Supporting documents and Linked records (owner 2026-11-02, "Document Attachments & Linked Records") ─────
// One card and one section, mounted in every document window and entry window by a single line (RecordEvidence), and behind a paperclip where a
// record has no window of its own: a bank line, a petty cash row, a supplier payment. What may be done is the SERVER's answer for each file
// (services/recordAttachments.ts), read as the card opens and again at the click, never worked out here.
//
// ⛔ EVERY INPUT AND EVERY DIALOG LIVES IN THIS FILE. testDirtyClose proves a read-only window has no field in its subtree and testFullscreenShell
// counts each pane's <Dialog: both hold, because a host mounts one line and nothing else. No upload waits on a Save, so no window here is ever dirty.
//
// ⛔ PREVIEW IS A BLOB, NEVER A FRAME. The server serves every file as an attachment with a sandbox CSP, so nothing uploaded can run on this origin;
// the viewer fetches the bytes and draws them with pdf.js or an <img>. Nothing has to be downloaded first, and nothing opens a tab.
//
// ⛔ PORTALLED TO THE BODY (relatedDocs.tsx RecordWindow's reason): a paperclip is held by a table cell, and a Dialog is not portalled itself.

/** nginx's body cap is 50 MB (nginx.prod.conf), and ten files of 15 MB are 150: several big files go as several requests, each all or nothing. */
const MAX_REQUEST_BYTES = 45 * 1024 * 1024;
const THUMB_MAX_BYTES = 8 * 1024 * 1024;
const THUMB_WIDTH = 320;
const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp']);
const APPROVED_EXTS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp', 'xls', 'xlsx', 'doc', 'docx', 'csv']);
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.doc,.docx,.csv';

/** UK day-first, the tenant's zone, to the minute: Documents.tsx voidedLine's own form. */
const whenOf = (sec: number): string => { const at = new Date(sec * 1000); return `${dmy(todayISO(at))} ${timeHM(at)}`; };
const extOf = (name: string): string => { const m = /\.([a-z0-9]{1,8})$/i.exec(String(name || '').trim()); return m ? m[1].toLowerCase() : ''; };
const typeLabel = (f: { name: string; mime: string }): string => (extOf(f.name) || String(f.mime || '').split('/').pop() || '').toUpperCase();
const sizeLabel = (n: number): string => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const iconOf = (f: { name: string; mime: string }): string => {
  const e = extOf(f.name);
  if (e === 'pdf' || /pdf/.test(f.mime)) return 'ti-file-type-pdf';
  if (IMAGE_EXTS.has(e) || /^image\//.test(f.mime)) return 'ti-photo';
  if (e === 'xls' || e === 'xlsx' || e === 'csv') return 'ti-file-spreadsheet';
  if (e === 'doc' || e === 'docx') return 'ti-file-text';
  return 'ti-file';
};

/** The first bytes of a file a person picked. */
const headOf = async (f: File, n = 512): Promise<Uint8Array> => new Uint8Array(await f.slice(0, n).arrayBuffer());
const startsWith = (b: Uint8Array, hex: string): boolean => hex.match(/../g)!.every((h, i) => b[i] === parseInt(h, 16));
const latin = (b: Uint8Array, from: number, to: number): string => Array.from(b.subarray(from, to)).map(c => String.fromCharCode(c)).join('');
/** The names in a ZIP's directory, [] for anything that is not a whole ZIP: services/fileSniff.ts zipEntryNames, line for line, because a project's two halves share no file. */
export function zipEntryNames(buf: Uint8Array): string[] {
  const v = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const EOCD = 0x06054b50, CEN = 0x02014b50;
  let at = -1;
  for (let i = buf.length - 22; i >= 0 && i >= buf.length - 22 - 65535; i--) { if (v.getUint32(i, true) === EOCD) { at = i; break; } }
  if (at < 0) return [];
  const count = v.getUint16(at + 10, true);
  let p = v.getUint32(at + 16, true);
  const names: string[] = [];
  const text = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (p + 46 > at || v.getUint32(p, true) !== CEN) return [];
    const nameLen = v.getUint16(p + 28, true), extraLen = v.getUint16(p + 30, true), commentLen = v.getUint16(p + 32, true);
    if (p + 46 + nameLen > at) return [];
    names.push(text.decode(buf.subarray(p + 46, p + 46 + nameLen)));
    p += 46 + nameLen + extraLen + commentLen;
  }
  return names;
}
/** The names in a compound file's directory, [] for anything that is not a whole one: services/fileSniff.ts cfbEntryNames, line for line. */
export function cfbEntryNames(buf: Uint8Array): string[] {
  if (buf.length < 512 || !startsWith(buf, 'd0cf11e0a1b11ae1')) return [];
  const v = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const size = 2 ** v.getUint16(30, true);
  if (size !== 512 && size !== 4096) return [];
  const isSector = (n: number): boolean => n < 0xfffffffa;
  const startOf = (n: number): number => (n + 1) * size;
  const hopsMax = Math.ceil(buf.length / size);
  const fat: number[] = [];
  for (let i = 0; i < 109; i++) { const n = v.getUint32(76 + i * 4, true); if (isSector(n)) fat.push(n); }
  let more = v.getUint32(68, true);
  for (let hops = 0; isSector(more); hops++) {
    if (hops > hopsMax || startOf(more) + size > buf.length) return [];
    for (let i = 0; i < size / 4 - 1; i++) { const n = v.getUint32(startOf(more) + i * 4, true); if (isSector(n)) fat.push(n); }
    more = v.getUint32(startOf(more) + size - 4, true);
  }
  const names: string[] = [];
  const text = new TextDecoder('utf-16le');
  let dir = v.getUint32(48, true);
  for (let hops = 0; isSector(dir); hops++) {
    if (hops > hopsMax || startOf(dir) + size > buf.length) return [];
    for (let e = startOf(dir); e + 128 <= startOf(dir) + size; e += 128) {
      const len = v.getUint16(e + 64, true);
      if (buf[e + 66] && len >= 2 && len <= 64) names.push(text.decode(buf.subarray(e, e + len - 2)));
    }
    const fatSector = fat[Math.floor(dir / (size / 4))];
    const link = fatSector === undefined ? -1 : startOf(fatSector) + (dir % (size / 4)) * 4;
    if (link < 0 || link + 4 > buf.length) return [];
    dir = v.getUint32(link, true);
  }
  return names;
}
// services/fileSniff.ts' rules, line for line, because a project's two halves share no file.
const ACTIVE_CONTENT = /<script|<html|<svg|<iframe|<object|<embed|<meta|<body|<!doctype|javascript:/i;
const CODE_HEAD = /^(?:<\?|<%|<!--)/;
const SCRIPT_HEAD = /^#!\s*\//;
const BINARY_HEAD = /^(?:PK[\x03\x05\x07]|MZ[\x00-\x08\x0e-\x1f\x90]|\x7fELF)/;
const textHead = (b: Uint8Array): string => latin(b, 0, Math.min(b.length, 1024)).replace(/^ï»¿/, '').replace(/\0/g, '').replace(/^[\sÿþ]+/, '');
const opensAsMarkup = (head: string): boolean => CODE_HEAD.test(head) || SCRIPT_HEAD.test(head) || BINARY_HEAD.test(head);
// By the decoder, not latin(): a 15 MB file's array of characters is more than a tab should be asked for. Only ASCII is looked for, and every decoder of this name leaves that alone.
const holdsActiveContent = (b: Uint8Array): boolean => ACTIVE_CONTENT.test(new TextDecoder('latin1').decode(b).replace(/\0/g, ''));
const XML_SPREADSHEET = /^<\?xml\b[^>]*\?>\s*(?:<\?mso-application\b[^>]*\?>\s*)?<(?:ss:)?Workbook\b[^>]*\bxmlns(?::ss)?\s*=\s*["']urn:schemas-microsoft-com:office:spreadsheet["']/i;
/** Do these bytes agree with the name? services/fileSniff.ts sniffApproved, the same rule on the same bytes (review 2026-11-03: a looser one here sent what the server then refused,
 *  and a refused write pages the admins), so a renamed file is told here and never posted. A workbook and a text file are read whole, which the size check before this keeps to 15 MB. */
async function bytesAgree(f: File, ext: string): Promise<boolean> {
  const b = await headOf(f, 1024);
  if (!b.length) return false;
  if (latin(b, 0, 4) === '%PDF') return ext === 'pdf';
  const picture = startsWith(b, 'ffd8') || startsWith(b, '89504e470d0a1a0a') || (latin(b, 0, 4) === 'RIFF' && latin(b, 8, 12) === 'WEBP');
  if (picture) return IMAGE_EXTS.has(ext);
  if (startsWith(b, 'd0cf11e0a1b11ae1')) {
    if (ext === 'doc' || ext === 'xls') return true;
    if (ext !== 'docx' && ext !== 'xlsx') return false;
    // A password-protected workbook or Word file.
    const parts = cfbEntryNames(new Uint8Array(await f.arrayBuffer()));
    return parts.includes('EncryptedPackage') && parts.includes('EncryptionInfo');
  }
  if (startsWith(b, '504b0304')) {
    if (ext !== 'docx' && ext !== 'xlsx' && ext !== 'xls') return false;
    const names = zipEntryNames(new Uint8Array(await f.arrayBuffer()));
    return names.includes('[Content_Types].xml') && names.some(n => n.startsWith(ext === 'docx' ? 'word/' : 'xl/'));
  }
  // A PDF with a BOM or a newline before its %PDF, as a reader allows in its first 1024 bytes.
  if (ext === 'pdf') {
    const at = latin(b, 0, b.length).indexOf('%PDF');
    if (at < 0) return false;
    const before = b.subarray(0, at);
    const lead = textHead(before);
    return !before.includes(0) && !lead.startsWith('<') && !opensAsMarkup(lead) && !holdsActiveContent(before);
  }
  // A CSV, the tab-separated text a bank's or an accountant's system saves as .xls, or Excel's XML Spreadsheet. A stray NUL stays refused: it is what keeps binary out.
  if (ext !== 'csv' && ext !== 'xls') return false;
  const whole = new Uint8Array(await f.arrayBuffer());
  const utf16 = startsWith(b, 'fffe') || startsWith(b, 'feff');
  if ((!utf16 && whole.includes(0)) || holdsActiveContent(whole)) return false;
  const head = textHead(b);
  if (ext === 'xls' && XML_SPREADSHEET.test(head)) return true;
  return !head.startsWith('%PDF') && !opensAsMarkup(head);
}
/** Why these files would be refused, in the reader's words, or ''. Type, size and count before POST: a refused write pages the admins. */
async function whyNotSend(files: File[], t: TFunction<'translation', undefined>): Promise<string> {
  if (files.length > ATT_MAX_FILES) return t('aw_att_too_many', { max: ATT_MAX_FILES });
  for (const f of files) {
    const ext = extOf(f.name);
    if (!APPROVED_EXTS.has(ext)) return t('aw_att_bad_type', { name: f.name });
    if (f.size > ATT_MAX_BYTES) return t('aw_att_too_big', { name: f.name, mb: ATT_MAX_BYTES / 1024 / 1024 });
    if (!await bytesAgree(f, ext)) return t('aw_att_bad_content', { name: f.name });
  }
  return '';
}

const batchesOf = (files: File[]): File[][] => {
  const out: File[][] = [];
  let at = 0;
  for (const f of files) {
    const last = out[out.length - 1];
    if (last && at + f.size <= MAX_REQUEST_BYTES) { last.push(f); at += f.size; } else { out.push([f]); at = f.size; }
  }
  return out;
};

/** A refusal, in the reader's language; the server's own sentence for a code this build has no words for. ⛔ Every key written in full, so a grep finds each. */
const WHY_KEY: Record<string, string> = {
  period_closed: 'aw_att_why_period_closed', month_filed: 'aw_att_why_month_filed', retained: 'aw_att_why_retained', not_yours: 'aw_att_why_not_yours',
  record_ended: 'aw_att_why_record_ended', unreadable: 'aw_att_why_unreadable', read_only: 'aw_ro_seat_refused', no_door: 'aw_att_why_no_door',
  entry_reversed: 'aw_att_why_entry_reversed', linked_period_closed: 'aw_att_why_linked_period_closed', linked_month_filed: 'aw_att_why_linked_month_filed',
};
const whyText = (t: TFunction<'translation', undefined>, w: AttRefusal | { code?: string; params?: any; why?: string } | null | undefined): string => {
  if (!w) return '';
  const code = String(w.code || '');
  const p: any = w.params || {};
  const month = /^\d{4}-\d{2}$/.test(String(p.month || '')) ? `${String(p.month).slice(5)}/${String(p.month).slice(0, 4)}` : '';
  return WHY_KEY[code] ? t(WHY_KEY[code], { from: p.from ? dmy(p.from) : '', to: p.to ? dmy(p.to) : '', month, until: p.until ? dmy(p.until) : '', docNo: String(p.docNo || '') }) : String(w.why || '');
};
/** What a refused request said, by its code when it has one (api.ts carries the body on the error). */
const errText = (t: TFunction<'translation', undefined>, e: any): string => {
  const p = e?.payload;
  if (p?.code === 'file_type') return t('aw_att_bad_content', { name: p.params?.name || '' });
  if (p?.code === 'file_too_large') return t('aw_att_too_big', { name: p.params?.name || '', mb: ATT_MAX_BYTES / 1024 / 1024 });
  if (p?.code === 'too_many_files') return t('aw_att_too_many', { max: ATT_MAX_FILES });
  if (p?.code && WHY_KEY[p.code]) return whyText(t, p);
  return String(e?.message || e) || t('aw_viewer_failed');
};

// ── The picture on a card ───────────────────────────────────────────────────────────────────────
/** A thumbnail once the card is on screen, the icon until then and for anything too big or not a picture. The bytes are read lazily, so a
 *  record with ten files costs ten requests only if somebody scrolls to them. */
function FileThumb({ clientId, f }: { clientId: string; f: AttFile }) {
  const [src, setSrc] = useState('');
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!f.preview || f.missing || f.size > THUMB_MAX_BYTES || typeof IntersectionObserver === 'undefined' || !box.current) return undefined;
    let alive = true;
    let objectUrl = '';
    const io = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      io.disconnect();
      attApi.blob(clientId, f.source, f.id, f.sheet)
        .then(async b => {
          if (b.type === 'application/pdf') { const u = await pdfThumbUrl(await b.arrayBuffer(), THUMB_WIDTH); if (alive) setSrc(u); return; }
          objectUrl = URL.createObjectURL(b);
          if (alive) setSrc(objectUrl);
        })
        .catch(() => { /* the icon stays: Preview is where a broken file says so */ });
    });
    io.observe(box.current);
    return () => { alive = false; io.disconnect(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [clientId, f.source, f.id, f.sheet, f.size, f.preview, f.missing]);
  return (
    <div ref={box} style={{ height: 118, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-gray-50)', borderBottom: '1px solid var(--border)', overflow: 'hidden' }}>
      {src ? <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }} />
        : <i className={`ti ${iconOf(f)}`} aria-hidden style={{ fontSize: 34, color: 'var(--text-tertiary)' }} />}
    </div>
  );
}

// ── Preview, in the system ──────────────────────────────────────────────────────────────────────
/** A file on screen: a PDF one page at a time with page navigation, a picture as it is, either at any zoom. What it is comes from the type the
 *  server served, which it read from the bytes. */
export function AttachmentViewer({ clientId, f, label, onClose }: { clientId: string; f: { source: string; id: string; sheet?: number; name: string }; label?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [pdf, setPdf] = useState<PdfHandle | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [page, setPage] = useState(1);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const area = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = '';
    let handle: PdfHandle | null = null;
    setFailed(false); setUrl(''); setPdf(null); setPage(1); setZoom(1);
    attApi.blob(clientId, f.source, f.id, f.sheet)
      .then(async b => {
        if (!alive) return;
        if (b.type === 'application/pdf') {
          handle = await openPdf(await b.arrayBuffer());
          if (alive) setPdf(handle); else handle.destroy();
        } else if (/^image\//.test(b.type)) {
          objectUrl = URL.createObjectURL(b);
          setUrl(objectUrl);
        } else setFailed(true);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); if (handle) handle.destroy(); };
  }, [clientId, f.source, f.id, f.sheet]);
  // 100% is the page filling the window's width, as a reader expects of a document; the zoom is on top of that.
  useEffect(() => {
    if (!pdf || !canvas.current) return;
    let alive = true;
    (async () => {
      // Its box's own width, less the box's padding, on a phone as on anything else: a floor here is a page cut off at the right.
      const width = Math.max(120, (area.current?.clientWidth || 800) - 24);
      const scale = (width / await pdf.pageWidth(page)) * zoom;
      if (alive && canvas.current) await pdf.render(page, canvas.current, scale);
    })().catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [pdf, page, zoom]);
  const at = ZOOMS.indexOf(zoom);
  const tool: React.CSSProperties = { ...actBtn, padding: '4px 9px' };
  return createPortal(
    <Dialog fullscreen title={<span style={{ overflowWrap: 'anywhere' }}>{f.name}</span>} docNo={label || undefined} onClose={onClose}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <DlBtn style={btn} onRun={() => attApi.download(clientId, f)}><i className="ti ti-download" aria-hidden /> {t('aw_att_download')}</DlBtn>
        </div>
      )}>
      <div ref={area} data-att-viewer={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: '100%' }}>
        {failed && <div style={errBox} data-att-view-failed="">{t('aw_att_view_fail')}</div>}
        {(pdf || url) && !failed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)', paddingBottom: 6 }}>
            <button type="button" style={tool} data-att-zoom="out" disabled={at <= 0} onClick={() => setZoom(ZOOMS[at - 1])} title={t('aw_att_zoom_out')} aria-label={t('aw_att_zoom_out')}><i className="ti ti-zoom-out" aria-hidden /></button>
            <span data-att-zoom-at="" style={{ fontSize: 12, minWidth: 42, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{Math.round(zoom * 100)}%</span>
            <button type="button" style={tool} data-att-zoom="in" disabled={at >= ZOOMS.length - 1} onClick={() => setZoom(ZOOMS[at + 1])} title={t('aw_att_zoom_in')} aria-label={t('aw_att_zoom_in')}><i className="ti ti-zoom-in" aria-hidden /></button>
            <button type="button" style={tool} data-att-zoom="fit" disabled={zoom === 1} onClick={() => setZoom(1)} title={t('aw_att_zoom_fit')} aria-label={t('aw_att_zoom_fit')}><i className="ti ti-arrows-horizontal" aria-hidden /></button>
            {pdf && pdf.pages > 1 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                <button type="button" style={tool} data-att-page="prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)} title={t('aw_att_prev_page')} aria-label={t('aw_att_prev_page')}><i className="ti ti-chevron-left" aria-hidden /></button>
                <span data-att-page-at="" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{t('aw_att_page', { n: page, of: pdf.pages })}</span>
                <button type="button" style={tool} data-att-page="next" disabled={page >= pdf.pages} onClick={() => setPage(p => p + 1)} title={t('aw_att_next_page')} aria-label={t('aw_att_next_page')}><i className="ti ti-chevron-right" aria-hidden /></button>
              </span>
            )}
          </div>
        )}
        {!pdf && !url && !failed && <Loader />}
        {!failed && (pdf || url) && (
          <div style={{ overflow: 'auto', textAlign: 'center', background: 'var(--c-gray-50)', borderRadius: 8, padding: 12, flex: 1 }}>
            {pdf ? <canvas ref={canvas} data-att-canvas="" style={{ background: '#fff', display: 'inline-block' }} />
              : <img src={url} alt={f.name} data-att-img="" style={{ width: `${zoom * 100}%`, maxWidth: zoom > 1 ? 'none' : '100%', display: 'inline-block' }} />}
          </div>
        )}
      </div>
    </Dialog>,
    document.body,
  );
}

// ── The card ────────────────────────────────────────────────────────────────────────────────────
function FileCard({ clientId, f, seatReads, onPreview, onRemove }: { clientId: string; f: AttFile; seatReads: boolean; onPreview: () => void; onRemove: () => void }) {
  const { t } = useTranslation();
  const small: React.CSSProperties = { ...actBtn, padding: '3px 7px', fontSize: 11.5 };
  return (
    <div data-att-file={f.id} data-att-source={f.source} data-att-own={f.own ? '1' : '0'}
      style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <FileThumb clientId={clientId} f={f} />
      <div style={{ padding: '7px 9px', display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
        <div data-att-name="" title={f.name} style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}{f.sheet > 1 ? ` (${f.sheet})` : ''}</div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}><span data-att-type="">{typeLabel(f)}</span> · {sizeLabel(f.size)}</div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }} data-att-by="">{f.at ? whenOf(f.at) : ''}{f.by ? `${f.at ? ' · ' : ''}${f.by}` : ''}</div>
        {/* A file of a linked record: the record it is on, whichever window shows it (the requirement's section 5). */}
        {!f.own && (
          <div data-att-from={f.record.id} style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${typeWord(t, f.record.kind)} ${f.record.docNo}`}>
            <i className="ti ti-link" aria-hidden /> {typeWord(t, f.record.kind)} {f.record.docNo}
          </div>
        )}
        {f.missing && <div data-att-missing="" style={{ fontSize: 11, color: '#B42318' }}>{t('aw_att_missing')}</div>}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 'auto', paddingTop: 4 }}>
          {f.preview && !f.missing && <button type="button" style={small} data-att-preview={f.id} onClick={onPreview}><i className="ti ti-eye" aria-hidden /> {t('aw_att_preview')}</button>}
          {!f.missing && <DlBtn style={small} onRun={() => attApi.download(clientId, f)}><i className="ti ti-download" aria-hidden /> {t('aw_att_download')}</DlBtn>}
          {f.canRemove && <button type="button" style={{ ...small, color: '#B42318' }} data-att-remove={f.id} onClick={onRemove}><i className="ti ti-trash" aria-hidden /> {t('aw_att_remove')}</button>}
        </div>
        {/* Why there is no Remove on a file of this record: a sentence, for a seat that could act on one. A read-only seat is told nothing it cannot use. */}
        {f.own && !f.canRemove && !!f.removeWhy && !seatReads && <div data-att-why={f.removeWhy.code} style={{ fontSize: 11, color: '#92400E', lineHeight: 1.45 }}>{whyText(t, f.removeWhy)}</div>}
      </div>
    </div>
  );
}

export function AttachmentsCard({ clientId, kind, id, tick = 0, onChanged }: { clientId: string; kind: string; id: string; tick?: number; onChanged?: () => void }) {
  const { t } = useTranslation();
  const seatReads = useReadOnlySeat();
  const [data, setData] = useState<AttList | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [viewing, setViewing] = useState<{ f: { source: string; id: string; sheet?: number; name: string }; label: string } | null>(null);
  const [removing, setRemoving] = useState<AttFile | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  const [own, setOwn] = useState(0);
  useEffect(() => {
    let alive = true;
    attApi.list(clientId, kind, id)
      .then((r: AttList) => { if (alive) setData(r); })
      .catch((e: any) => { if (alive) setErr(errText(t, e)); });
    return () => { alive = false; };
  }, [clientId, kind, id, tick, own]);   // eslint-disable-line react-hooks/exhaustive-deps
  const changed = () => { setOwn(n => n + 1); if (onChanged) onChanged(); };
  // A drop that misses the tile, on a file's card or anywhere else in the window, must not let the browser navigate the whole app to the file: this card sits inside edit windows that
  // hold typed work. BankStatement/index.tsx holds the same, for as long as its page is up; this, for as long as a card is.
  useEffect(() => {
    const guard = (e: DragEvent) => { e.preventDefault(); };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => { window.removeEventListener('dragover', guard); window.removeEventListener('drop', guard); };
  }, []);

  const send = async (picked: File[]) => {
    if (!picked.length || busy) return;
    setErr('');
    setBusy(true);
    let sent = 0;
    try {
      const why = await whyNotSend(picked, t);
      if (why) { setErr(why); return; }
      // This window may have stood open for minutes, and a refused write pages the admins: asked again at the click.
      const v = await attApi.list(clientId, kind, id);
      setData(v);
      if (!v.found) { setErr(t('aw_att_gone')); return; }
      if (!v.canAdd) { setErr(t('aw_att_why_record_ended')); return; }
      for (const batch of batchesOf(picked)) { await attApi.upload(clientId, kind, id, batch); sent += 1; }
    } catch (e: any) { setErr(errText(t, e)); } finally { setBusy(false); if (sent) changed(); }
  };

  const canAdd = !!data?.canAdd && !seatReads;
  const files = data?.files || [];
  const removed = data?.removed || [];
  return (
    <div data-att-card={kind}>
      <div style={sectionHead}>{t('aw_att_title')}{files.length ? ` (${files.length})` : ''}</div>
      {err && <div style={{ ...errBox, marginBottom: 8 }} data-att-err="">{err}</div>}
      {!data && !err ? <Loader /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(176px, 1fr))', gap: 10 }}>
          {canAdd && (
            <div data-att-drop="" role="button" tabIndex={0} aria-busy={busy}
              onClick={() => { if (!busy) picker.current?.click(); }}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !busy) { e.preventDefault(); picker.current?.click(); } }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
              onDragLeave={() => setOver(false)}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); setOver(false); void send(Array.from(e.dataTransfer.files || [])); }}
              style={{ border: `1.5px dashed ${over ? 'var(--accent)' : 'var(--border-strong)'}`, borderRadius: 8, padding: 12, minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, textAlign: 'center', cursor: busy ? 'wait' : 'pointer', background: over ? 'var(--accent-bg)' : 'transparent' }}>
              <i className={busy ? 'ti ti-loader' : 'ti ti-upload'} aria-hidden style={{ fontSize: 20, color: 'var(--text-tertiary)' }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{busy ? t('aw_att_uploading') : t('aw_att_drop')}</div>
              <div style={{ ...note, lineHeight: 1.35 }}>{t('aw_att_drop_hint', { max: ATT_MAX_FILES, mb: ATT_MAX_BYTES / 1024 / 1024 })}</div>
              <input ref={picker} type="file" multiple accept={ACCEPT} data-att-input="" style={{ display: 'none' }}
                onChange={e => { const list = Array.from(e.target.files || []); e.target.value = ''; void send(list); }} />
            </div>
          )}
          {files.map(f => (
            <FileCard key={`${f.source}:${f.id}:${f.sheet}`} clientId={clientId} f={f} seatReads={seatReads}
              onPreview={() => setViewing({ f, label: f.own ? '' : `${typeWord(t, f.record.kind)} ${f.record.docNo}` })}
              onRemove={() => setRemoving(f)} />
          ))}
          {!!data && !files.length && !canAdd && <div data-att-none="" style={{ ...card, ...note, gridColumn: '1 / -1' }}>{t('aw_att_none')}</div>}
        </div>
      )}
      {data?.truncated && <div style={{ ...note, marginTop: 4 }}>{t('aw_rel_truncated', { n: files.length })}</div>}
      {/* The convention of every list here: what was removed stays on the page, struck, with who, when and why. */}
      {!!removed.length && (
        <div data-att-removed="" style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('aw_att_removed_title')}</div>
          {removed.map(r => (
            <div key={`${r.source}:${r.id}`} data-att-removed-row={r.id} data-att-removed-source={r.source} style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
              {r.canRead && r.preview
                ? <button type="button" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', textDecoration: 'line-through', fontSize: 'inherit' }} onClick={() => setViewing({ f: { source: r.source, id: r.id, name: r.name }, label: t('aw_att_removed_title') })}>{r.name}</button>
                : <span style={{ textDecoration: 'line-through' }}>{r.name}</span>}
              {' '}{t('aw_att_removed_line', { by: r.by || '—', when: r.at ? whenOf(r.at) : '—', reason: r.reason || '—' })}
            </div>
          ))}
        </div>
      )}
      {viewing && <AttachmentViewer clientId={clientId} f={viewing.f} label={viewing.label} onClose={() => setViewing(null)} />}
      {removing && createPortal(
        <ReasonDialog title={t('aw_att_remove_title')} docNo={removing.name} line={t('aw_att_remove_line', { name: removing.name, no: removing.record.docNo || '—' })}
          hint={t('aw_att_remove_hint')} icon="ti ti-trash" label={t('aw_att_remove_btn')} onClose={() => setRemoving(null)}
          onSubmit={async reason => {
            // Asked again at the click (a period may have been closed since this window opened): a refused DELETE pages the admins.
            const v = await attApi.list(clientId, kind, id);
            const now = (v.files || []).find(x => x.source === removing.source && x.id === removing.id);
            if (!now || !now.canRemove) {
              setData(v);
              throw new Error(now ? whyText(t, now.removeWhy) : t('aw_att_gone'));
            }
            await attApi.remove(clientId, removing.source, removing.id, reason);
            setRemoving(null);
            changed();
          }} />,
        document.body,
      )}
    </div>
  );
}

// ── Linked records ──────────────────────────────────────────────────────────────────────────────
/** The window a linked record opens in needs a little of the shell's frame, and this section is mounted where no shell's ctx reaches (an entry window, a
 *  voucher's document): the client and the seat are all those windows read, and they read it to choose what to draw. */
function useFrame(clientId: string): AwCtx {
  const me = useStore(s => s.me);
  return useMemo(() => ({ clientId, me: me || { role: '', modules: [] }, reload: 0, bump: () => undefined, setNav: () => undefined, setPeriod: () => undefined, refreshPeriods: () => undefined, periods: [], periodsLoading: false, periodId: '', period: null, periodClosed: false, clientName: '', summary: null, summaryErr: '' }) as unknown as AwCtx, [clientId, me]);
}

export function LinkedRecords({ clientId, kind, id, tick = 0, onChanged }: { clientId: string; kind: string; id: string; tick?: number; onChanged?: () => void }) {
  const { t } = useTranslation();
  const seatReads = useReadOnlySeat();
  const frame = useFrame(clientId);
  const { openRecord, recordWindow } = useOpenRecord(frame);
  const [data, setData] = useState<LinkedAnswer | null>(null);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<LinkedDoc[] | null>(null);
  const [busy, setBusy] = useState('');
  const [own, setOwn] = useState(0);
  useEffect(() => {
    let alive = true;
    attApi.links(clientId, kind, id)
      .then((r: LinkedAnswer) => { if (alive) setData(r); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [clientId, kind, id, tick, own]);
  // The search runs as somebody types, a moment after they stop: every keystroke would be a scan of the company's numbers.
  useEffect(() => {
    if (!adding || term.trim().length < 2) { setHits(null); return undefined; }
    let alive = true;
    const timer = window.setTimeout(() => {
      attApi.search(clientId, term.trim()).then((r: { rows: LinkedDoc[] }) => { if (alive) setHits(r.rows || []); }).catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    }, 250);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [adding, term, clientId]);
  const changed = () => { setOwn(n => n + 1); if (onChanged) onChanged(); };
  const docs = data?.docs || [];
  // Never offered: what is linked already, and the record itself (a link to itself is refused).
  const held = new Set([...docs.map(d => d.id), ...(data?.self?.ids || [])]);
  const link = async (d: LinkedDoc) => {
    if (busy) return;
    setBusy(d.id); setErr('');
    try {
      const v = await attApi.links(clientId, kind, id);
      if (!v.found) { setErr(t('aw_att_gone')); return; }
      await attApi.link(clientId, { kind, id }, { kind: d.kind, id: d.id });
      setTerm(''); setHits(null); setAdding(false);
      changed();
    } catch (e: any) { setErr(String(e?.message || e)); } finally { setBusy(''); }
  };
  const unlink = async (d: LinkedDoc) => {
    if (busy || !d.link || !window.confirm(t('aw_lr_unlink_confirm'))) return;
    setBusy(d.id); setErr('');
    try { await attApi.unlink(clientId, d.link.id); changed(); } catch (e: any) { setErr(String(e?.message || e)); } finally { setBusy(''); }
  };
  return (
    <div data-linked-records={kind}>
      <div style={sectionHead}>{t('aw_lr_title')}{docs.length ? ` (${docs.length})` : ''}</div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      <div style={{ ...card, padding: 5 }}>
        {!data && !err && <Loader />}
        {!!data && !docs.length && <div data-linked-none="" style={{ padding: '6px 8px', fontSize: 12, color: 'var(--text-tertiary)' }}>{t('aw_lr_none')}</div>}
        {docs.map(d => (
          <div key={`${d.kind}:${d.id}`} data-linked-row={d.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <RelatedRow d={d} onOpen={r => openRecord(r.kind, r.id, r.docNo, r.record, r.voided)} />
              {!!d.link && <div style={{ ...note, padding: '0 8px 5px' }}>{t('aw_lr_by', { by: d.link.by || '—', when: d.link.at ? whenOf(d.link.at) : '—' })}</div>}
            </div>
            {!!d.link && !seatReads && (
              <button type="button" data-linked-unlink={d.id} disabled={!!busy} onClick={() => { void unlink(d); }} style={{ ...actBtn, padding: '3px 7px', fontSize: 11.5, marginTop: 6, flex: 'none' }}>
                <i className={busy === d.id ? 'ti ti-loader' : 'ti ti-unlink'} aria-hidden /> {t('aw_lr_unlink')}
              </button>
            )}
          </div>
        ))}
        {data?.truncated && <div style={{ ...note, padding: '4px 8px' }}>{t('aw_rel_truncated', { n: docs.length })}</div>}
        {!!data?.canLink && !seatReads && (
          <div style={{ padding: '6px 8px', borderTop: docs.length ? '1px solid var(--border)' : undefined }}>
            {!adding ? (
              <button type="button" data-linked-add="" onClick={() => setAdding(true)} style={{ ...actBtn, padding: '3px 8px', fontSize: 11.5 }}><i className="ti ti-link" aria-hidden /> {t('aw_lr_add')}</button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input autoFocus value={term} data-linked-search="" onChange={e => setTerm(e.target.value)} placeholder={t('aw_lr_search_ph')} aria-label={t('aw_lr_search_ph')} style={{ ...field, flex: 1, minWidth: 0 }} />
                  <button type="button" onClick={() => { setAdding(false); setTerm(''); }} style={{ ...actBtn, flex: 'none' }} aria-label={t('aw_dlg_close')} title={t('aw_dlg_close')}><i className="ti ti-x" aria-hidden /></button>
                </div>
                {hits && !hits.length && <div data-linked-no-hits="" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('aw_lr_search_none')}</div>}
                {(hits || []).filter(h => !held.has(h.id)).map(h => (
                  <div key={`${h.kind}:${h.id}`} data-linked-hit={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ color: 'var(--text-secondary)', flex: 'none' }}>{typeWord(t, h.kind, h.noteKind)}</span>
                    <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h.docNo}>{h.docNo || t('aw_rel_no_number')}</span>
                    {!!h.date && <span style={{ color: 'var(--text-tertiary)', fontSize: 11.5, flex: 'none' }}>{dmy(h.date)}</span>}
                    <div style={{ flex: 1 }} />
                    <button type="button" data-linked-pick={h.id} disabled={!!busy} onClick={() => { void link(h); }} style={{ ...actBtn, padding: '3px 8px', fontSize: 11.5, flex: 'none' }}>
                      <i className={busy === h.id ? 'ti ti-loader' : 'ti ti-link'} aria-hidden /> {t('aw_lr_link')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {recordWindow}
    </div>
  );
}

// ── The trail ───────────────────────────────────────────────────────────────────────────────────
const TRAIL_KEY: Record<string, string> = {
  'file:upload': 'aw_att_trail_upload', 'file:delete': 'aw_att_trail_delete', 'file:read': 'aw_att_trail_read', 'link:create': 'aw_att_trail_link', 'link:delete': 'aw_att_trail_unlink',
};
/** Who did what to this record's files and links, for whoever must answer for them. Read-only and read only when asked: most windows are never asked. No field in it, so a read-only window stays one. */
function RecordTrail({ clientId, kind, id, tick }: { clientId: string; kind: string; id: string; tick: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AttTrail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    attApi.trail(clientId, kind, id, page).then((r: AttTrail) => { if (alive) { setData(r); setErr(''); } }).catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [open, clientId, kind, id, page, tick]);
  return (
    <div data-att-trail={open ? 'open' : 'closed'}>
      <button type="button" data-att-trail-toggle="" onClick={() => setOpen(o => !o)} style={{ ...actBtn, padding: '3px 8px', fontSize: 11.5 }}>
        <i className={open ? 'ti ti-chevron-down' : 'ti ti-chevron-right'} aria-hidden /> {t('aw_att_trail_title')}
      </button>
      {open && err && <div style={{ ...errBox, marginTop: 6 }}>{err}</div>}
      {open && !data && !err && <Loader />}
      {open && !!data && !data.rows.length && <div data-att-trail-none="" style={{ ...note, marginTop: 6 }}>{t('aw_att_trail_none')}</div>}
      {open && !!data?.rows.length && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {data.rows.map((r, i) => (
            <div key={`${r.at}-${i}`} data-att-trail-row={`${r.kind}:${r.action}`} style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              {whenOf(r.at)} · {r.by || '—'} · {TRAIL_KEY[`${r.kind}:${r.action}`] ? t(TRAIL_KEY[`${r.kind}:${r.action}`]) : r.action}{r.what ? `: ${r.what}` : ''}
            </div>
          ))}
          <Pagination page={data.page} total={data.total} limit={data.limit || 100} onPage={setPage} />
        </div>
      )}
    </div>
  );
}

/** The card and the section are drawn only where their routes answer (RecordEvidence says why). Whoever offers a way to them asks this first. */
export const useEvidenceOn = (): boolean => {
  const me = useStore(s => s.me);
  return !!me?.acctWorkspace && (me.modules || []).includes('accounting_books');
};

/**
 * The one line a window mounts: its record's files and what it is linked to. `kind` is a stored doc_type, a list's family key, 'expense' with the
 * register's key as the id, or a kind of its own: purchase_order, purchase_note, reimbursement, journal_entry, bank_line, cash_row, expense_payment,
 * payable_payment, payable, expense_upload. The two sections read each other's writes: a link brings the other record's files into the card.
 */
export function RecordEvidence({ clientId, kind, id }: { clientId: string; kind: string; id: string }) {
  const on = useEvidenceOn();
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick(n => n + 1), []);
  // ⛔ An entry's window is also opened from the classic Bookkeeping pages, on installations with no workspace: every route behind this answers
  // those a 404 with a JSON body, which pages the admins, so there the section is simply not drawn.
  if (!on) return null;
  return (
    <div data-record-evidence={kind} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <AttachmentsCard clientId={clientId} kind={kind} id={id} tick={tick} onChanged={bump} />
      <LinkedRecords clientId={clientId} kind={kind} id={id} tick={tick} onChanged={bump} />
      <RecordTrail clientId={clientId} kind={kind} id={id} tick={tick} />
    </div>
  );
}

// ── The paperclip ───────────────────────────────────────────────────────────────────────────────
/** The window behind a paperclip, and what Linked Records opens a bank line or a petty cash row in. Not portalled: both callers hang it off the body themselves. */
export function EvidenceWindow({ clientId, kind, id, docNo, onClose }: { clientId: string; kind: string; id: string; docNo?: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog fullscreen title={t('aw_att_title')} docNo={docNo || undefined} onClose={onClose}>
      <RecordEvidence clientId={clientId} kind={kind} id={id} />
    </Dialog>
  );
}

/** A record with no window of its own (a bank line, a petty cash row, a supplier payment): the same card and section, in a window from this file.
 *  sourceDoc.tsx's ruling stands: these have no document screen, and this is not one. `count`: the row's own files, from the list (useAttachmentCounts). */
export function AttachmentsBtn({ clientId, kind, id, docNo, count, onClosed }: { clientId: string; kind: string; id: string; docNo?: string; count?: number; onClosed?: () => void }) {
  const { t } = useTranslation();
  const on = useEvidenceOn();
  const [open, setOpen] = useState(false);
  const label = t('aw_att_paperclip');
  // Nothing behind it where the card is not drawn: a paperclip that opens an empty window is worse than none.
  if (!on) return null;
  return (
    // ⛔ Everything hangs off this element in React's tree, the portal included, so a click anywhere in it is never the row's.
    <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', verticalAlign: 'baseline' }}>
      <button type="button" data-att-clip={id} className={TAP_TARGET} onClick={() => setOpen(true)} title={label} aria-label={label}
        style={{ background: 'none', border: 'none', padding: '1px 4px', cursor: 'pointer', color: count ? 'var(--accent)' : 'var(--text-tertiary)', fontSize: 13, lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <i className="ti ti-paperclip" aria-hidden />{count ? <span data-att-clip-count="" style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{count}</span> : null}
      </button>
      {open && createPortal(
        <EvidenceWindow clientId={clientId} kind={kind} id={id} docNo={docNo} onClose={() => { setOpen(false); if (onClosed) onClosed(); }} />,
        document.body,
      )}
    </span>
  );
}

/** How many files each row of a list's page holds, in one request: what its paperclips show. `refresh`, after a paperclip's window closes. */
export function useAttachmentCounts(clientId: string, kind: string, ids: string[]): { counts: Record<string, number>; refresh: () => void } {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [tick, setTick] = useState(0);
  const key = ids.join(',');
  useEffect(() => {
    let alive = true;
    if (!key) { setCounts({}); return undefined; }
    attApi.counts(clientId, kind, key.split(','))
      .then((r: { counts: Record<string, number> }) => { if (alive) setCounts(r.counts || {}); })
      .catch(() => { /* a paperclip with no number still opens */ });
    return () => { alive = false; };
  }, [clientId, kind, key, tick]);
  return { counts, refresh: () => setTick(n => n + 1) };
}
